import { parseISO } from 'date-fns';
import { capitalize, formatISODate } from '../../../../../../common/utils/common.util';
import { ReportTemplateData } from './notesTemplates.util';
import { BudgetSummaryDTO, DrawSummary, ProjectLineItemDTO } from '../../../../../project/project.type';
import {
  SectionFieldCollection,
  CoverPageDTO,
  CoverPageBudgetRowDTO,
  ScheduleGraphRowDTO,
} from '../../../../magic-report/magic-report.type';
import { formatCurrency } from '../../../../../../common/utils/common.util';
import { prepareGraphData, renderPercentCompleteGraph, renderScheduleGraph, renderReallocationGraph } from './coverPageGraphs.util';
import { ChangeOrderRow } from '../../../../magic-report/sections/ChangeOrdersTable';
import { orEmpty, joinNonEmpty } from '../../../../../../common/utils/textFormat.util';
import type { StoredMaterialRow } from '../../../../magic-report/sections/StoredMaterialsTable';
import { parseDrawWaivers, LienWaiverStoredStatus } from '../../../../magic-report/sections/lien-waivers-draws.util';
import { isCoLienHidden, resolveHiddenCoLienDrawIds } from '../../../../magic-report/sections/co-lien-visibility.util';
import { roundCompletionPercent } from '../../../utils/percent.util';

export interface CoverPagePhotoData {
  url: string;
  caption?: string;
}

/** One covered payapp (G702 = draw = budget) as the cover page table needs to see it. */
export interface CoverPageDrawContext {
  drawId?: string;
  drawSummary?: Partial<DrawSummary>;
  budgetSummary?: Partial<BudgetSummaryDTO>;
  lineItems?: ProjectLineItemDTO[];
}

export interface CoverPageTableData {
  budgetSummary?: Partial<BudgetSummaryDTO>;
  drawSummary?: Partial<DrawSummary>;
  coverPage?: CoverPageDTO | null;
  scheduleSection?: SectionFieldCollection | null;
  changeOrdersSection?: SectionFieldCollection | null;
  lienWaiversSection?: SectionFieldCollection | null;
  reallocationsSection?: SectionFieldCollection | null;
  storedMaterialsSection?: SectionFieldCollection | null;
  siteVisitDate?: string;
  lineItems?: ProjectLineItemDTO[];
  scheduleGraph?: ScheduleGraphRowDTO[];
  scheduleGraphAxis?: import('../../../../magic-report/magic-report.type').ScheduleGraphAxisDTO;
  /**
   * Every payapp the report covers, oldest application first. When this holds more than one entry
   * the table switches to its multi-payapp form (see `buildCoverPageTableHTML`). Reports covering a
   * single payapp may omit it entirely — the singular `budgetSummary`/`drawSummary` still drive the
   * table exactly as before.
   */
  draws?: CoverPageDrawContext[];
  /**
   * Payapps whose Change Orders and Lien Waivers sections the report hides. The change-order and
   * lien-release rows below aggregate across payapps, so they must skip these — a figure the
   * report never shows anywhere else has no business appearing on the cover page.
   */
  hiddenCoLienDrawIds?: string[];
}

/**
 * Helper to get field value from section
 */
const getFieldValue = (section: SectionFieldCollection | null | undefined, fieldName: string): string => {
  if (!section?.fields) return '';
  const field = section.fields.find(f => f.fieldName === fieldName);
  return field?.value || '';
};

/**
 * Reads back a currency string the Lien Waivers section stored ("$1,234.56", "(1,234.56)", "1234").
 * Returns null when there is no number to read, so callers can tell "nothing saved" from "$0".
 */
const parseStoredAmount = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const isNegative = trimmed.startsWith('(') && trimmed.endsWith(')');
  const numeric = Number(trimmed.replace(/[()$,\s]/g, ''));
  if (!isFinite(numeric)) return null;
  return isNegative ? -numeric : numeric;
};

/**
 * The release amount as the Lien Waivers section computed it, so the cover page prints the same
 * number as the report body.
 *
 * The section derives each payapp's amount from the full Cumulative? × Thru Curr. Amt? matrix and
 * lets the user hand-edit it (see `getLienWaiverTableRow`). Neither the second toggle nor a manual
 * edit is recoverable from the budget summary, which is why re-deriving here drifted. Multi-draw
 * reports sum the unconditional payapps, matching the detail rows printed in the body.
 *
 * Returns null when the section has stored nothing — a report saved before it wrote an amount — and
 * the caller falls back to the budget-derived value.
 */
const getStoredLienWaiverAmount = (section: SectionFieldCollection | null | undefined, hiddenDrawIds: Set<string>): number | null => {
  const waivers = parseDrawWaivers(getFieldValue(section, 'LienWaiverDraws')).filter(
    waiver => !isCoLienHidden(hiddenDrawIds, waiver.drawId),
  );
  if (waivers.length > 0) {
    const amounts = waivers
      .filter(waiver => waiver.status === 'Unconditional')
      .map(waiver => parseStoredAmount(waiver.amount))
      .filter((amount): amount is number => amount !== null);
    return amounts.length > 0 ? amounts.reduce((sum, amount) => sum + amount, 0) : null;
  }
  // Pre-multi-draw reports only carry the scalar mirror of the primary payapp.
  return parseStoredAmount(getFieldValue(section, 'LienWaiverAmount'));
};

export const isValidChangeOrderRow = (row: unknown): row is Pick<ChangeOrderRow, 'prevCO' | 'currCO' | 'drawId'> => {
  if (row === null || typeof row !== 'object' || Array.isArray(row)) return false;
  const { prevCO, currCO } = row as Record<string, unknown>;
  return typeof prevCO === 'number' && isFinite(prevCO) && typeof currCO === 'number' && isFinite(currCO);
};

/**
 * Format date for display (DD-MMM-YY format like "31-May-24")
 */
const formatDateShort = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '';
  try {
    // parseISO treats date-only strings (yyyy-MM-dd) as local midnight, avoiding
    // the UTC shift that causes off-by-one in US timezones.
    const date = parseISO(dateStr);
    if (isNaN(date.getTime())) return '';
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear().toString().slice(-2);
    return `${day}-${month}-${year}`;
  } catch {
    return '';
  }
};

/** Placeholder printed by the application-# / period-ending rows when the payapps disagree. */
const MULTIPLE_APPS_TEXT = 'Multiple apps; See Report';

/**
 * Collapses one field across the covered payapps: the shared value when they all agree (and there
 * is one), otherwise the "see the report" placeholder. Blanks are ignored so a payapp that never
 * had, say, a period ending date does not force the placeholder on by itself.
 */
const sameOrMultiple = (values: Array<string | undefined | null>): string => {
  const present = Array.from(new Set(values.map(v => (v ?? '').trim()).filter(Boolean)));
  if (present.length === 0) return '';
  return present.length === 1 ? present[0] : MULTIPLE_APPS_TEXT;
};

/** Line items a payapp actually contributes to the printed totals. */
const contributingLineItems = (draws: CoverPageDrawContext[]): ProjectLineItemDTO[] =>
  draws.flatMap(draw => (draw.lineItems || []).filter(item => !item.isDeleted));

const sumBy = <T>(items: T[], select: (item: T) => number | null | undefined): number =>
  items.reduce((total, item) => total + (Number(select(item)) || 0), 0);

/**
 * One line item's total completed and stored to date — previously billed plus what CDCC approved
 * this period. Mirrors how `calculateBudgetSummaryFromLineItems` builds `totalCompletedAndStored`,
 * including its fallback for line items that carry no explicit CDCC-approved amount, so the cover
 * page's weighted % complete matches the G702 figure the budget module shows.
 */
const completedAndStoredToDate = (item: ProjectLineItemDTO): number => {
  const cdccApproved =
    typeof item.cdccApproved === 'number' ? item.cdccApproved : Number(item.completeThisPeriod || 0) + Number(item.storedThisPeriod || 0);
  return Number(item.previousApplication || 0) + Number(cdccApproved || 0);
};

/**
 * A weighted-average percentage: the numerator total over the denominator total, NOT the mean of
 * the per-payapp percentages. Returns null when there is nothing to divide by, so the caller can
 * leave the row blank rather than print 0%.
 */
const weightedPercent = (numerator: number, denominator: number): number | null =>
  denominator === 0 ? null : (numerator / denominator) * 100;

/**
 * One payapp's Hard Cost % Complete (Amount Recommended): the reviewer's typed value when they
 * overrode it in the Cover Page module, otherwise CDCC completed & stored to date ÷ revised budget
 * off that payapp's line items — so it includes reductions made via CDCC Approved. The budget
 * summary only stands in when no line items are loaded. A single-payapp report's row may predate
 * draw-tagging, so it falls back to the module's first row.
 */
const resolveDrawPercentComplete = (
  draw: CoverPageDrawContext,
  budgetRows: CoverPageBudgetRowDTO[],
  isOnlyDraw: boolean,
): number | null => {
  const drawId = draw.drawId ?? draw.drawSummary?.id;
  const row = budgetRows.find(r => r.drawId && r.drawId === drawId) ?? (isOnlyDraw ? budgetRows[0] : undefined);
  if (row?.isPercentCompleteOverridden && row.percentComplete != null) return row.percentComplete;
  const lineItems = contributingLineItems([draw]);
  return (
    weightedPercent(
      sumBy(lineItems, completedAndStoredToDate),
      sumBy(lineItems, item => item.revisedBudget),
    ) ??
    draw.budgetSummary?.totalContractBudgetPerComplete ??
    null
  );
};

/** Every stored-materials row saved on the report, across all payapps (rows carry their own drawId). */
const parseStoredMaterialRows = (section: SectionFieldCollection | null | undefined): StoredMaterialRow[] => {
  const rowsField = section?.fields?.find(f => f.fieldName === 'StoredMaterialsRows');
  return (rowsField?.entries || [])
    .map(entry => {
      if (!entry.value) return null;
      try {
        return JSON.parse(entry.value) as StoredMaterialRow;
      } catch {
        return null;
      }
    })
    .filter((row): row is StoredMaterialRow => row !== null);
};

/**
 * The Lien Release wording for a multi-payapp report, per the client's rule: one shared status
 * prints as itself; a mix that contains at least one unconditional waiver prints "Not All
 * Unconditional"; any other mix prints "No Unconditional".
 */
export const summarizeLienReleaseStatuses = (statuses: LienWaiverStoredStatus[]): string => {
  const displayOf = (status: LienWaiverStoredStatus): string => (status === 'Not Received' ? 'None Received' : status);
  if (statuses.length === 0) return 'None Received';

  const distinct = Array.from(new Set(statuses));
  if (distinct.length === 1) return displayOf(distinct[0]);
  return distinct.includes('Unconditional') ? 'Not All Unconditional' : 'No Unconditional';
};

/**
 * Everything `buildCoverPageTableHTML` needs to explain itself in the console.
 */
interface CoverPageTableLogInput {
  isMultiPayapp: boolean;
  coveredDraws: CoverPageDrawContext[];
  allLineItems: ProjectLineItemDTO[];
  budgetRows: CoverPageBudgetRowDTO[];
  changeOrderRows: Array<Pick<ChangeOrderRow, 'prevCO' | 'currCO'>>;
  changeOrdersThisPeriodValue: number;
  changeOrdersPreviousValue: number;
  storedRows: StoredMaterialRow[];
  offSiteStoredValue: number;
  onSiteStoredValue: number;
  drawWaivers: ReturnType<typeof parseDrawWaivers>;
  lienWaiverStatus: string;
  retainageHeldSum: number;
  revisedBudgetSum: number;
  printed: Record<string, string>;
}

/**
 * Dumps the full derivation of the cover page table to the console: each covered payapp's raw
 * contribution, the line-item aggregates behind the two weighted percentages, the supporting
 * section rows (change orders / stored materials / lien waivers), and finally the formatted value
 * printed on each row.
 *
 * Deliberately unconditional — this runs at export time only (once per generated report), and the
 * whole point is to be able to read it back out of a user's browser log after the fact. Everything
 * sits inside one collapsed group so it costs a single line until it is expanded.
 */
const logCoverPageTableValues = (input: CoverPageTableLogInput): void => {
  const {
    isMultiPayapp,
    coveredDraws,
    allLineItems,
    budgetRows,
    changeOrderRows,
    changeOrdersThisPeriodValue,
    changeOrdersPreviousValue,
    storedRows,
    offSiteStoredValue,
    onSiteStoredValue,
    drawWaivers,
    lienWaiverStatus,
    retainageHeldSum,
    revisedBudgetSum,
    printed,
  } = input;

  try {
    const mode = isMultiPayapp ? `MULTI-PAYAPP (${coveredDraws.length} payapps)` : 'SINGLE PAYAPP';
    /* eslint-disable no-console */
    console.groupCollapsed(`%c[CoverPageTable] ${mode} — expand for every field and how it was summed`, 'font-weight:bold');

    console.log(
      `Mode: ${mode}. Aggregation (summing, weighted percentages, "Total …" labels) applies only when more than one payapp is covered.`,
    );

    // ── Per-payapp raw inputs ────────────────────────────────────────────────
    console.groupCollapsed(`1. Per-payapp inputs — every BudgetSummary field the table reads (${coveredDraws.length} payapp(s))`);
    console.table(
      coveredDraws.map((draw, index) => ({
        '#': index + 1,
        drawId: draw.drawId ?? '(none)',
        'drawSummary.name': draw.drawSummary?.name ?? '',
        'drawSummary.applicationNumber': draw.drawSummary?.applicationNumber ?? '',
        'drawSummary.periodEnding': draw.drawSummary?.periodEnding ?? '',
        'bs.totalAmountReccForFunding': draw.budgetSummary?.totalAmountReccForFunding ?? null,
        'bs.requestedAmount': draw.budgetSummary?.requestedAmount ?? null,
        'bs.difference': draw.budgetSummary?.difference ?? null,
        'bs.totalRetainageHeld': draw.budgetSummary?.totalRetainageHeld ?? null,
        'bs.totalCompletedAndStored': draw.budgetSummary?.totalCompletedAndStored ?? null,
        'bs.originalContractBudgetSum': draw.budgetSummary?.originalContractBudgetSum ?? null,
        'bs.contractBudgetSumToDate': draw.budgetSummary?.contractBudgetSumToDate ?? null,
        'bs.balanceToCompletePlusRetainage': draw.budgetSummary?.balanceToCompletePlusRetainage ?? null,
        'bs.totalContractBudgetPerComplete': draw.budgetSummary?.totalContractBudgetPerComplete ?? null,
        'lineItems (not deleted)': (draw.lineItems || []).filter(item => !item.isDeleted).length,
      })),
    );
    console.groupEnd();

    // ── Sums taken over those inputs ─────────────────────────────────────────
    const sumOf = (select: (summary: Partial<BudgetSummaryDTO>) => number | undefined): number =>
      sumBy(
        coveredDraws.map(draw => draw.budgetSummary || {}),
        select,
      );
    console.groupCollapsed('2. Sums across payapps — Σ of each BudgetSummary field above');
    console.table([
      {
        'Table row': 'Recommended Hard Cost To Fund',
        'Source field': 'bs.totalAmountReccForFunding',
        Σ: sumOf(s => s.totalAmountReccForFunding),
      },
      { 'Table row': 'Funding Requested', 'Source field': 'bs.requestedAmount', Σ: sumOf(s => s.requestedAmount) },
      { 'Table row': 'Difference', 'Source field': 'bs.difference', Σ: sumOf(s => s.difference) },
      { 'Table row': 'Retainage Held (amount)', 'Source field': 'bs.totalRetainageHeld', Σ: retainageHeldSum },
      { 'Table row': 'Retainage rate denominator', 'Source field': 'bs.totalCompletedAndStored', Σ: sumOf(s => s.totalCompletedAndStored) },
      {
        'Table row': 'Original Hard Cost Budget',
        'Source field': 'bs.originalContractBudgetSum',
        Σ: sumOf(s => s.originalContractBudgetSum),
      },
      { 'Table row': 'Revised Hard Cost Budget', 'Source field': 'bs.contractBudgetSumToDate', Σ: sumOf(s => s.contractBudgetSumToDate) },
      {
        'Table row': 'Budget Remaining (Incl. Retainage)',
        'Source field': 'bs.balanceToCompletePlusRetainage',
        Σ: sumOf(s => s.balanceToCompletePlusRetainage),
      },
    ]);
    console.log(
      `Retainage rate is WEIGHTED, not averaged: |Σ totalRetainageHeld| ÷ Σ totalCompletedAndStored = ` +
        `|${retainageHeldSum}| ÷ ${sumOf(s => s.totalCompletedAndStored)}`,
    );
    console.groupEnd();

    // ── Cover Page module rows ───────────────────────────────────────────────
    console.groupCollapsed(
      `3. Cover Page module budget rows (${budgetRows.length}) — source for "Recommended Hard Cost To Fund", "Total Funding Requested" and "Total Difference" when multi-payapp`,
    );
    if (budgetRows.length === 0) {
      console.log("No budget rows saved on coverPage — those three rows fall back to the Σ of the payapps' own BudgetSummary fields.");
    } else {
      console.table(
        budgetRows.map(row => ({
          budgetName: row.budgetName,
          drawId: row.drawId ?? '(hand-added row)',
          requestedAmount: row.requestedAmount,
          amountRecommendedForFunding: row.amountRecommendedForFunding,
          percentComplete: row.percentComplete,
        })),
      );
      const rowsRecommendedSum = sumBy(budgetRows, r => r.amountRecommendedForFunding);
      const rowsRequestedSum = sumBy(budgetRows, r => r.requestedAmount);
      console.log(
        `Σ amountRecommendedForFunding = ${rowsRecommendedSum}, Σ requestedAmount = ${rowsRequestedSum}, ` +
          `Difference = ${rowsRecommendedSum} − ${rowsRequestedSum} = ${rowsRecommendedSum - rowsRequestedSum}`,
      );
    }
    console.groupEnd();

    // ── Line-item aggregates behind the weighted percentages ─────────────────
    const cdccApprovedSum = sumBy(allLineItems, item => item.cdccApproved);
    const previousApplicationSum = sumBy(allLineItems, item => item.previousApplication);
    const completeThisPeriodSum = sumBy(allLineItems, item => item.completeThisPeriod);
    const storedThisPeriodSum = sumBy(allLineItems, item => item.storedThisPeriod);
    const completedAndStoredSum = sumBy(allLineItems, completedAndStoredToDate);
    console.groupCollapsed(`4. Line-item aggregates (${allLineItems.length} line items across all payapps) — drive the two weighted %`);
    console.table([
      { Field: 'li.revisedBudget', Σ: revisedBudgetSum, 'Used for': 'denominator of both percentages' },
      {
        Field: 'li.previousApplication + li.cdccApproved (completed & stored to date)',
        Σ: completedAndStoredSum,
        'Used for': 'numerator of Hard Cost % Complete',
      },
      { Field: 'li.cdccApproved', Σ: cdccApprovedSum, 'Used for': 'part of completed & stored to date' },
      { Field: 'li.previousApplication', Σ: previousApplicationSum, 'Used for': 'both numerators' },
      { Field: 'li.completeThisPeriod', Σ: completeThisPeriodSum, 'Used for': 'numerator of Percentage Requested' },
      { Field: 'li.storedThisPeriod', Σ: storedThisPeriodSum, 'Used for': 'numerator of Percentage Requested' },
    ]);
    console.log(
      `Hard Cost % Complete = Σ completed & stored to date ÷ Σ revisedBudget = ${completedAndStoredSum} ÷ ${revisedBudgetSum} = ` +
        `${weightedPercent(completedAndStoredSum, revisedBudgetSum)}%`,
    );
    console.log(
      `Percentage Requested = Σ (previousApplication + completeThisPeriod + storedThisPeriod) ÷ Σ revisedBudget = ` +
        `(${previousApplicationSum} + ${completeThisPeriodSum} + ${storedThisPeriodSum}) ÷ ${revisedBudgetSum} = ` +
        `${weightedPercent(previousApplicationSum + completeThisPeriodSum + storedThisPeriodSum, revisedBudgetSum)}%`,
    );
    console.groupEnd();

    // ── Change orders ────────────────────────────────────────────────────────
    console.groupCollapsed(`5. Change Orders section — ${changeOrderRows.length} ChangeOrdersRows entries (all payapps share this field)`);
    console.table(changeOrderRows.map((row, index) => ({ '#': index + 1, prevCO: row.prevCO, currCO: row.currCO })));
    console.log(`Σ currCO (Change Order(s) this Period) = ${changeOrdersThisPeriodValue}`);
    console.log(`Σ prevCO (Change Order(s) Previous) = ${changeOrdersPreviousValue}`);
    console.log(`Current Hard Cost Changes = Σ currCO only = ${changeOrdersThisPeriodValue} (same rule for single and multi payapp)`);
    console.log(
      `Revised Hard Cost Budget still folds in previous COs: Original + Σ currCO + Σ prevCO = ` +
        `${changeOrdersThisPeriodValue + changeOrdersPreviousValue} of change orders`,
    );
    console.groupEnd();

    // ── Stored materials ─────────────────────────────────────────────────────
    console.groupCollapsed(`6. Stored Materials section — ${storedRows.length} StoredMaterialsRows entries (each tagged with its drawId)`);
    console.table(
      storedRows.map((row, index) => ({
        '#': index + 1,
        drawId: row.drawId ?? '(untagged)',
        description: row.description,
        storedThisPeriod: row.storedThisPeriod,
        storedOffsite: row.storedOffsite,
      })),
    );
    console.log(`Σ storedThisPeriod where storedOffsite = "Yes" (Off-Site) = ${offSiteStoredValue}`);
    console.log(`Σ storedThisPeriod where storedOffsite ≠ "Yes" (On-Site, from G703) = ${onSiteStoredValue}`);
    console.log(`Total Stored Materials = ${offSiteStoredValue + onSiteStoredValue}`);
    if (!isMultiPayapp) {
      console.log('Single payapp: these rows still print the pre-existing "None" placeholder — the sums above are informational only.');
    }
    console.groupEnd();

    // ── Lien waivers ─────────────────────────────────────────────────────────
    console.groupCollapsed(`7. Lien Waivers section — ${drawWaivers.length} per-payapp entries in LienWaiverDraws`);
    if (drawWaivers.length === 0) {
      console.log(`No per-payapp waivers stored; falling back to the scalar LienWaiverStatus field = "${lienWaiverStatus}"`);
    } else {
      console.table(
        drawWaivers.map(waiver => ({
          drawId: waiver.drawId,
          drawName: waiver.drawName,
          status: waiver.status,
          amount: waiver.amount,
          isCumulative: waiver.isCumulative,
          throughCurrentPeriod: waiver.throughCurrentPeriod,
        })),
      );
      console.log(
        `Lien Release rule → all equal: show it · any-but-not-all Unconditional: "Not All Unconditional" · otherwise: "No Unconditional". ` +
          `Release Amount = Σ amount of the Unconditional rows only.`,
      );
    }
    console.groupEnd();

    // ── What actually got printed ────────────────────────────────────────────
    console.groupCollapsed('8. Final printed values — what each row of the cover page table shows');
    console.table(Object.entries(printed).map(([row, value]) => ({ 'Table row': row, 'Printed value': value || '(Not Provided)' })));
    console.groupEnd();

    console.groupEnd();
    /* eslint-enable no-console */
  } catch {
    // Diagnostics must never break an export.
  }
};

/**
 * Build the cover page table HTML with two columns.
 *
 * MULTI-PAYAPP REPORTS (more than one covered payapp / G702 / budget) print an aggregated variant:
 * every payapp-derived figure is summed across the payapps, the two percentage rows become weighted
 * averages, and three labels change ("Total Funding Requested", "Total Difference", "Total
 * Retainage Held"). Schedule, contingency and sufficient-balance rows are unchanged — they are not
 * per-payapp. A report covering a single payapp prints exactly what it printed before this variant
 * existed; every branch below is guarded on `isMultiPayapp` for that reason.
 */
export const buildCoverPageTableHTML = (data: CoverPageTableData): string => {
  const { budgetSummary, drawSummary, coverPage, scheduleSection, changeOrdersSection, lienWaiversSection, siteVisitDate } = data;

  // The payapps this table covers. Callers that predate multi-payapp pass only the singular
  // budget/draw, which becomes a one-entry list and leaves every aggregation below a no-op.
  const coveredDraws: CoverPageDrawContext[] = data.draws?.length
    ? data.draws
    : [{ drawSummary, budgetSummary, lineItems: data.lineItems }];
  const isMultiPayapp = coveredDraws.length > 1;
  // Payapps hidden from Change Orders / Lien Waivers. Only those two families of rows below
  // consult it; every other aggregation still covers all payapps.
  const coLienHiddenDrawIds = resolveHiddenCoLienDrawIds(
    data.hiddenCoLienDrawIds,
    coveredDraws.map(d => d.drawSummary?.id),
  );
  const budgetSummaries = coveredDraws.map(draw => draw.budgetSummary).filter(Boolean) as Array<Partial<BudgetSummaryDTO>>;
  const allLineItems = contributingLineItems(coveredDraws);

  /** Σ of one budget-summary figure over every covered payapp. */
  const sumBudgets = (select: (summary: Partial<BudgetSummaryDTO>) => number | undefined): number => sumBy(budgetSummaries, select);

  // Cover Page module rows are already one-per-payapp (plus any hand-added budgets), so the two
  // "see Amount for Cover Page Module above" rows read straight off them. Reports whose module has
  // no rows yet fall back to the payapps' own budget summaries.
  const budgetRows = coverPage?.budgetRows || [];
  const sumBudgetRows = (select: (row: CoverPageBudgetRowDTO) => number | undefined, fallback: number): number =>
    budgetRows.length > 0 ? sumBy(budgetRows, select) : fallback;

  // Helper to format currency (always two decimal places for export/download)
  const formatCurrencyValue = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '';
    return formatCurrency(value.toString(), true, false, true);
  };

  // Helper to format percentage (rounded to nearest whole number)
  const formatPercent = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '';
    return `${Math.round(value)}%`;
  };
  // % Complete / % Requested keep a decimal just below 100% (see roundCompletionPercent)
  const formatCompletionPercent = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '';
    return `${roundCompletionPercent(value)}%`;
  };

  // Multi-payapp reports print what the Cover Page module's table adds up to — the reviewer edits
  // and hand-added budgets there are the source of truth, not the G702/3s. The payapps' own summed
  // budget figures only stand in for a module that has no rows saved yet.
  const budgetRowsRecommendedSum = sumBudgetRows(
    row => row.amountRecommendedForFunding,
    sumBudgets(s => s.totalAmountReccForFunding),
  );
  const budgetRowsRequestedSum = sumBudgetRows(
    row => row.requestedAmount,
    sumBudgets(s => s.requestedAmount),
  );

  // Get values from G702/3 (BudgetSummary). Single payapp: the one budget, as before. Multi-payapp:
  // summed across every covered payapp.
  const recommendedHardCostToFund = formatCurrencyValue(
    isMultiPayapp ? budgetRowsRecommendedSum : budgetSummary?.totalAmountReccForFunding,
  );
  const contractorsApplicationNumber = isMultiPayapp
    ? sameOrMultiple(coveredDraws.map(draw => draw.drawSummary?.applicationNumber))
    : drawSummary?.applicationNumber || '';
  const periodEndingDate = isMultiPayapp
    ? sameOrMultiple(coveredDraws.map(draw => formatDateShort(draw.drawSummary?.periodEnding)))
    : formatDateShort(drawSummary?.periodEnding);
  const fundingRequested = formatCurrencyValue(isMultiPayapp ? budgetRowsRequestedSum : budgetSummary?.requestedAmount);
  // Difference stays consistent with the two rows above it: recommended − requested off the same
  // table, rather than Σ of the payapps' own G702-derived differences.
  const difference = formatCurrencyValue(isMultiPayapp ? budgetRowsRecommendedSum - budgetRowsRequestedSum : budgetSummary?.difference);
  // Multi-payapp retainage rate is weighted (total held ÷ total completed & stored), not the mean of
  // the per-payapp rates — a payapp with a tiny balance would otherwise skew it as much as the
  // largest one. `totalRetainageHeld` is stored negative, hence the abs.
  const retainageHeldSum = sumBudgets(s => s.totalRetainageHeld);
  const currentRetainageHeldPercent = formatPercent(
    isMultiPayapp
      ? weightedPercent(
          Math.abs(retainageHeldSum),
          sumBudgets(s => s.totalCompletedAndStored),
        )
      : budgetSummary?.totalRetainageHeldPercentage,
  );
  const currentRetainageHeldAmount = formatCurrencyValue(isMultiPayapp ? retainageHeldSum : budgetSummary?.totalRetainageHeld);
  // Percentage Requested (Amount Requested): (previous + this period + stored this period) over the
  // revised budget, across every payapp's line items. Uses what the contractor billed, so it is
  // independent of reductions made via CDCC Approved. The Percent Requested bar prints this too.
  const revisedBudgetSum = sumBy(allLineItems, item => item.revisedBudget);
  const percentageRequestedValue = weightedPercent(
    sumBy(allLineItems, item => (item.previousApplication || 0) + (item.completeThisPeriod || 0) + (item.storedThisPeriod || 0)),
    revisedBudgetSum,
  );
  const percentageRequested = formatCompletionPercent(percentageRequestedValue);
  const originalHardCostBudget = formatCurrencyValue(
    isMultiPayapp ? sumBudgets(s => s.originalContractBudgetSum) : budgetSummary?.originalContractBudgetSum,
  );
  const balanceToComplete = formatCurrencyValue(
    isMultiPayapp ? sumBudgets(s => s.balanceToCompletePlusRetainage) : budgetSummary?.balanceToCompletePlusRetainage,
  );

  // Hard Cost % Complete: each payapp's own % Complete (typed override or line-item formula).
  // Multi-payapp reports weight them by revised budget — with no overrides that is exactly total
  // completed-and-stored-to-date ÷ total revised budget. The Percent Complete bar prints this too.
  const drawPercents = coveredDraws.map(draw => ({
    percent: resolveDrawPercentComplete(draw, budgetRows, !isMultiPayapp),
    revisedBudget: sumBy(contributingLineItems([draw]), item => item.revisedBudget),
  }));
  const hardCostPercentCompleteValue = isMultiPayapp
    ? weightedPercent(
        // Each payapp's share of completed & stored, back in dollars (its % is already ×100).
        sumBy(drawPercents, d => ((d.percent ?? 0) / 100) * d.revisedBudget),
        revisedBudgetSum,
      )
    : drawPercents[0].percent;
  const hardCostPercentComplete = formatCompletionPercent(hardCostPercentCompleteValue);
  const contingencyUsed = coverPage?.contingencyPercentageUsed == null ? 'N/A' : formatPercent(coverPage.contingencyPercentageUsed);
  const sufficientBalance =
    coverPage?.isBalanceSufficient !== null && coverPage?.isBalanceSufficient !== undefined
      ? coverPage.isBalanceSufficient
        ? 'Yes'
        : 'No'
      : '';

  // Get values from Schedule module
  const weeksBehindToReport = getFieldValue(scheduleSection, 'WeeksBehindToReport');
  // Check if value is a valid number (not an error message or empty)
  const weeksBehindNum =
    weeksBehindToReport && !isNaN(parseFloat(weeksBehindToReport)) && !weeksBehindToReport.toLowerCase().includes('cannot calculate')
      ? parseFloat(weeksBehindToReport)
      : null;
  const onSchedule = weeksBehindNum !== null && weeksBehindNum > 0 ? 'No' : 'Yes';
  const commencementDate = formatDateShort(getFieldValue(scheduleSection, 'CommencementDate'));
  // When the schedule has no Commencement Date, fall back to the inspector's estimate and relabel the row
  const estimatedCommencementDate = formatDateShort(getFieldValue(scheduleSection, 'CDCCEstimatedCommencementDate'));
  const commencementRow = commencementDate
    ? { label: 'Commencement Date', value: commencementDate }
    : { label: 'Estimated Commencement Date', value: estimatedCommencementDate };
  const originalCompletionDate = formatDateShort(getFieldValue(scheduleSection, 'OriginalCompletionDate'));
  const revisedCompletionDateValue = getFieldValue(scheduleSection, 'RevisedCompletionDate');
  const revisedCompletionDate = revisedCompletionDateValue
    ? formatDateShort(revisedCompletionDateValue)
    : originalCompletionDate
      ? 'No revisions'
      : '';
  const cdccEstimatedCompletionDate = formatDateShort(getFieldValue(scheduleSection, 'CDCCEstimatedCompletionDate'));
  const certificateOfOccupancyValue = getFieldValue(scheduleSection, 'CertificateOfOccupancyIssued');
  const certificateOfOccupancyIssued = certificateOfOccupancyValue || 'None';

  // Get values from Change Orders module
  // Sum prevCO/currCO across the Change Orders line-items table (ChangeOrdersRows entries)
  const changeOrdersRowsField = changeOrdersSection?.fields?.find(f => f.fieldName === 'ChangeOrdersRows');
  const changeOrderRows: Array<Pick<ChangeOrderRow, 'prevCO' | 'currCO' | 'drawId'>> =
    changeOrdersRowsField?.entries
      ?.map(entry => {
        if (!entry.value) return null;
        try {
          return JSON.parse(entry.value);
        } catch {
          return null;
        }
      })
      .filter(isValidChangeOrderRow)
      .filter(row => !isCoLienHidden(coLienHiddenDrawIds, row.drawId)) || [];
  const changeOrdersThisPeriodValue = changeOrderRows.reduce((sum, row) => sum + (row.currCO ?? 0), 0);
  const changeOrdersPreviousValue = changeOrderRows.reduce((sum, row) => sum + (row.prevCO ?? 0), 0);
  const changeOrdersThisPeriod = formatCurrencyValue(changeOrdersThisPeriodValue);
  const changeOrdersPrevious = formatCurrencyValue(changeOrdersPreviousValue);
  // This row mirrors "Change Order(s) this Period" exactly, for both single and multi payapp:
  // the client wants that for multi-payapp, and including previous COs here was corrected as a bug
  // for single payapp. Note this is NOT the rule for Revised Hard Cost Budget below, which does
  // still fold in previous COs.
  const currentHardCostChanges = formatCurrencyValue(changeOrdersThisPeriodValue);

  // Revised Hard Cost Budget. Multi-payapp reads the payapps' own revised budgets (Σ
  // contractBudgetSumToDate) rather than re-deriving Original + COs, so it matches the G702s.
  const originalHardCostBudgetValue = budgetSummary?.originalContractBudgetSum || 0;
  const revisedHardCostBudgetValue = isMultiPayapp
    ? sumBudgets(s => s.contractBudgetSumToDate)
    : originalHardCostBudgetValue + changeOrdersThisPeriodValue + changeOrdersPreviousValue;
  const revisedHardCostBudget = formatCurrencyValue(revisedHardCostBudgetValue);

  // Get values from Lien Waivers module. Every payapp carries its own status inside the single
  // `LienWaiverDraws` field, so a multi-payapp report collapses those per the client's rule instead
  // of reading the primary payapp's scalar mirror.
  const drawWaivers = parseDrawWaivers(getFieldValue(lienWaiversSection, 'LienWaiverDraws')).filter(
    waiver => !isCoLienHidden(coLienHiddenDrawIds, waiver.drawId),
  );
  const lienWaiverStatus = getFieldValue(lienWaiversSection, 'LienWaiverStatus') || 'None Received';
  const lienRelease =
    isMultiPayapp && drawWaivers.length > 0
      ? summarizeLienReleaseStatuses(drawWaivers.map(waiver => waiver.status))
      : lienWaiverStatus === 'Unconditional'
        ? 'Unconditional'
        : lienWaiverStatus === 'Conditional'
          ? 'Conditional'
          : 'None Received';
  // "No unconditional waiver anywhere" is the multi-payapp equivalent of the single-payapp
  // Not Received case: there is no release amount to print.
  const lienWaiversNotPresent =
    isMultiPayapp && drawWaivers.length > 0
      ? drawWaivers.every(waiver => waiver.status === 'Not Received')
      : lienWaiverStatus === 'Not Received';
  // Any unconditional waiver means the row prints the summed unconditional amount, even when the
  // heading reads "Not All Unconditional".
  const hasUnconditionalWaiver =
    isMultiPayapp && drawWaivers.length > 0
      ? drawWaivers.some(waiver => waiver.status === 'Unconditional')
      : lienRelease === 'Unconditional';

  // Release amount for unconditional waivers: whatever the Lien Waivers section saved, so this
  // matches the body. Only reports predating that stored amount fall back to deriving it here from
  // IsCumulative — lessPreviousFundings when cumulative, otherwise the current requested amount.
  let releaseAmountThisPeriod: string;
  if (lienWaiversNotPresent) {
    releaseAmountThisPeriod = 'N/A';
  } else if (hasUnconditionalWaiver) {
    let amount = getStoredLienWaiverAmount(lienWaiversSection, coLienHiddenDrawIds);

    if (amount === null) {
      const isCumulative = getFieldValue(lienWaiversSection, 'IsCumulative') === 'true';
      amount = (isCumulative ? budgetSummary?.lessPreviousFundings : budgetSummary?.requestedAmount) ?? null;
    }

    releaseAmountThisPeriod = formatCurrencyValue(amount);
  } else {
    releaseAmountThisPeriod = 'Conditional';
  }

  const siteVisit = siteVisitDate ? formatDateShort(siteVisitDate) : '';

  // Stored Materials. Rows carry their own drawId and all payapps share one `StoredMaterialsRows`
  // field, so summing the field already sums across payapps. Single-payapp reports keep the
  // pre-existing "None" placeholder — wiring real numbers there is a separate change.
  const storedRows = parseStoredMaterialRows(data.storedMaterialsSection);
  const offSiteStoredValue = sumBy(
    storedRows.filter(row => row.storedOffsite === 'Yes'),
    row => row.storedThisPeriod,
  );
  const onSiteStoredValue = sumBy(
    storedRows.filter(row => row.storedOffsite !== 'Yes'),
    row => row.storedThisPeriod,
  );
  let offSiteStoredMaterials = 'None';
  let onSiteStoredMaterials = 'None';
  let totalStoredMaterials = 'None';
  if (isMultiPayapp) {
    offSiteStoredMaterials = offSiteStoredValue ? formatCurrencyValue(offSiteStoredValue) : 'None';
    onSiteStoredMaterials = onSiteStoredValue ? formatCurrencyValue(onSiteStoredValue) : 'None';
    totalStoredMaterials = offSiteStoredValue + onSiteStoredValue ? formatCurrencyValue(offSiteStoredValue + onSiteStoredValue) : 'None';
  }

  // Build a section with heading and fields
  const buildSection = (mainLabel: string, mainValue: string, fields: Array<{ label: string; value: string }>) => {
    const mainRow = `
      <div style="margin-bottom: 15px; margin-top: 15px;">
        <div style="display: flex; justify-content: space-between; margin-bottom: 6px;">
          <span style="color: #1976d2; font-weight: bold; font-size: 18px;">${mainLabel}:</span>
          <span style="color: #d32f2f; font-weight: bold; font-size: 18px;">${mainValue || 'Not Provided'}</span>
        </div>
        <div style="border-bottom: 1px solid #e0e0e0; margin-bottom: 8px;"></div>
        ${fields
          .map(
            field => `
          <div style="display: flex; justify-content: space-between; margin-left: 15px; margin-bottom: 6px; line-height: 1.2;">
            <span style="color: #000000; font-size: 17px;">${field.label}:</span>
            <span style="color: #000000; font-size: 17px;">${field.value || 'Not Provided'}</span>
          </div>
        `,
          )
          .join('')}
      </div>
    `;
    return mainRow;
  };

  // Trace every figure the table prints: which payapp each number came from, which field it read,
  // and how the multi-payapp aggregations were derived. Collapsed into one console group per render
  // so it stays readable when an export builds several reports in a row.
  logCoverPageTableValues({
    isMultiPayapp,
    coveredDraws,
    allLineItems,
    budgetRows,
    changeOrderRows,
    changeOrdersThisPeriodValue,
    changeOrdersPreviousValue,
    storedRows,
    offSiteStoredValue,
    onSiteStoredValue,
    drawWaivers,
    lienWaiverStatus,
    retainageHeldSum,
    revisedBudgetSum,
    printed: {
      'Recommended Hard Cost To Fund': recommendedHardCostToFund,
      "Contractor's Application #": contractorsApplicationNumber,
      'Period Ending Date': periodEndingDate,
      'Site Visit Date': siteVisit,
      'Funding Requested': fundingRequested,
      Difference: difference,
      'Retainage Held': [currentRetainageHeldPercent, currentRetainageHeldAmount].filter(Boolean).join('   '),
      'Hard Cost % Complete': hardCostPercentComplete,
      'Percentage Requested': percentageRequested,
      'Contingency Used': contingencyUsed,
      'Current Hard Cost Changes': currentHardCostChanges,
      'Original Hard Cost Budget': originalHardCostBudget,
      'Change Order(s) this Period': changeOrdersThisPeriod,
      'Change Order(s) Previous': changeOrdersPrevious,
      'Revised Hard Cost Budget': revisedHardCostBudget,
      'Off-Site Stored Materials': offSiteStoredMaterials,
      'On-Site Stored Materials': onSiteStoredMaterials,
      'Total Stored Materials': totalStoredMaterials,
      'Sufficient Balance': sufficientBalance,
      'Budget Remaining (Incl. Retainage)': balanceToComplete,
      'Lien Release': lienRelease,
      'Release Amount this Period': releaseAmountThisPeriod,
    },
  });

  // Column 1 sections
  const column1Sections = [
    buildSection('Recommended Hard Cost To Fund', recommendedHardCostToFund, [
      { label: isMultiPayapp ? "Contractors' Application #'s" : "Contractor's Application #", value: contractorsApplicationNumber },
      { label: isMultiPayapp ? 'Period Ending Dates' : 'Period Ending Date', value: periodEndingDate },
      { label: 'Site Visit Date', value: siteVisit },
      { label: isMultiPayapp ? 'Total Funding Requested' : 'Funding Requested', value: fundingRequested },
      { label: isMultiPayapp ? 'Total Difference' : 'Difference', value: difference },
      {
        label: isMultiPayapp ? 'Total Retainage Held' : 'Current Retainage Held',
        value: [currentRetainageHeldPercent, currentRetainageHeldAmount].filter(Boolean).join('   '),
      },
    ]),
    buildSection('Hard Cost % Complete', hardCostPercentComplete, [
      { label: 'Percentage Requested', value: percentageRequested },
      { label: 'Contingency Used', value: contingencyUsed },
    ]),
    buildSection('Current Hard Cost Changes', currentHardCostChanges, [
      { label: 'Original Hard Cost Budget', value: originalHardCostBudget },
      { label: 'Change Order(s) this Period', value: changeOrdersThisPeriod },
      { label: 'Change Order(s) Previous', value: changeOrdersPrevious },
      { label: 'Revised Hard Cost Budget', value: revisedHardCostBudget },
    ]),
  ].join('');

  // Column 2 sections
  const column2Sections = [
    buildSection('On Schedule', onSchedule, [
      { label: 'Weeks Behind', value: weeksBehindNum !== null ? weeksBehindToReport : '' },
      commencementRow,
      { label: 'Original Completion Date', value: originalCompletionDate },
      { label: 'Revised Completion Date', value: revisedCompletionDate },
      { label: 'CDCC Estimated Completion Date', value: cdccEstimatedCompletionDate },
      { label: 'Certificate of Occupancy Issued', value: certificateOfOccupancyIssued },
    ]),
    buildSection('Off-Site Stored Materials', offSiteStoredMaterials, [
      { label: 'On-Site Stored Materials (from G703)', value: onSiteStoredMaterials },
      { label: 'Total Stored Materials', value: totalStoredMaterials },
    ]),
    buildSection('Sufficient Balance', sufficientBalance, [{ label: 'Budget Remaining (Including Retainage)', value: balanceToComplete }]),
    buildSection('Lien Release', lienRelease, [{ label: 'Release Amount this Period', value: releaseAmountThisPeriod }]),
  ].join('');

  // Build graphs section if graph type is specified
  let graphsHTML = '';
  const graphType = coverPage?.graphType || 'standard';

  if (graphType !== 'none') {
    const graphData = prepareGraphData(data, data.lineItems, data.scheduleGraph, data.scheduleGraphAxis, {
      percentComplete: hardCostPercentCompleteValue ?? 0,
      percentRequested: percentageRequestedValue ?? 0,
    });

    if (graphType === 'standard') {
      // Standard: %Complete (left) + Schedule (right)
      graphsHTML = `
        <div class="cover-page-graphs" style="margin-top: 20px; display: flex; gap: 20px; width: 100%; align-items: flex-start; page-break-inside: avoid; break-inside: avoid;">
          <div style="flex: 1; display: flex; align-items: flex-start; justify-content: flex-start;">
            ${renderPercentCompleteGraph(graphData)}
          </div>
          <div style="flex: 1; display: flex; align-items: flex-start; justify-content: flex-start;">
            ${renderScheduleGraph(graphData)}
          </div>
        </div>
      `;
    } else if (graphType === 'reallocations') {
      // Reallocations: %Complete (left) + Reallocations (right)
      graphsHTML = `
        <div class="cover-page-graphs" style="margin-top: 20px; display: flex; gap: 20px; width: 100%; align-items: flex-start; page-break-inside: avoid; break-inside: avoid;">
          <div style="flex: 1; display: flex; align-items: flex-start; justify-content: flex-start;">
            ${renderPercentCompleteGraph(graphData)}
          </div>
          <div style="flex: 1; display: flex; align-items: flex-start; justify-content: flex-start;">
            ${renderReallocationGraph(graphData)}
          </div>
        </div>
      `;
    }
  }

  return `
    <div class="magic-cover-page-table" style="margin-top: 5px; padding: 0 10px;">
      <div style="display: flex; gap: 30px; width: 100%; font-family: Times New Roman, serif;">
        <!-- First Column -->
        <div style="flex: 1;">
          ${column1Sections}
        </div>
        <!-- Second Column -->
        <div style="flex: 1;">
          ${column2Sections}
        </div>
      </div>
      ${graphsHTML}
    </div>
  `;
};

/**
 * Build simple header-style cover page for magic report
 * Displays project information in header format without labels
 */
export const buildMagicReportCoverPageHTML = (
  data: ReportTemplateData,
  photos?: CoverPagePhotoData[],
  tableData?: CoverPageTableData,
): string => {
  const projectName = orEmpty(data.projectSummary?.projectTitle);
  const projectStreet = orEmpty(data.projectSummary?.projectAddress?.street);
  const projectCityState = joinNonEmpty([
    capitalize(data.projectSummary?.projectAddress?.city),
    (data.projectSummary?.projectAddress?.state || '').toUpperCase(),
  ]);
  // Row 1 right: street address if present, otherwise fall back to city/state
  const addressLine1 = projectStreet || projectCityState;
  // Row 2 right: city/state, only shown if the street address was already shown above
  const addressLine2 = projectStreet ? projectCityState : '';
  const applicationNumber = orEmpty(data.drawSummary?.applicationNumber);
  // Use report date from Site Conditions; if not set or invalid, show '--'
  const rawReportDate = data.siteConditions?.reportDate;
  const reportDate = rawReportDate ? formatISODate(rawReportDate) || '--' : '--';
  const projectNumber = orEmpty(data.projectSummary?.projectNumber);

  // Build photos section if photos are provided
  const photosHTML = photos && photos.length > 0 ? buildCoverPagePhotosHTML(photos) : '';

  // Build table section if table data is provided
  const tableHTML = tableData ? buildCoverPageTableHTML(tableData) : '';

  return `
    <div class="magic-report-cover-page" style="font-family: Times New Roman, serif; width: 100%; display: flex; flex-direction: column;">
      <!-- Header Section -->
      <div style="display: flex; justify-content: space-between; align-items: flex-start; padding: 5px 5px;">
        <!-- Left Side -->
        <div style="flex: 1;">
          <div style="margin-bottom: 6px;">
            <div style="font-size: 23px; font-weight: bold; color: #000;">${projectName}</div>
          </div>
          <div style="margin-bottom: 4px;">
            <div style="font-size: 23px; color: #000;">Draw #${applicationNumber}</div>
          </div>
          <div style="display: flex; margin-bottom: 2px;">
            <div style="font-size: 17px; color: #000; flex: 0 0 200px;">CDCC Project #: ${projectNumber}</div>
            <div style="font-size: 17px; color: #000;">${addressLine1}</div>
          </div>
          <div style="display: flex;">
            <div style="font-size: 17px; color: #000; flex: 0 0 200px;">Report: ${reportDate}</div>
            <div style="font-size: 17px; color: #000;">${addressLine2}</div>
          </div>
        </div>

        <!-- Right Side -->
        <!-- The source logo PNG has ~18% transparent padding baked in above/below the mark,
             so the visible box is cropped to the actual artwork via negative margin + overflow:hidden -->
        <div class="company-logo-wrap" style="flex: 0 0 auto; margin: 0; padding: 0; line-height: 0; height: 90px; overflow: hidden;">
          <img alt="CD Construction Consulting" class="company-logo" style="height: 140px; width: auto; display: block; margin: -25px 0 0 0; padding: 0; vertical-align: top;" />
        </div>
      </div>
      
      <!-- Photos Section -->
      ${photosHTML}
      
      <!-- Table Section (below images) -->
      ${tableHTML}
    </div>
  `;
};

/**
 * Build HTML for cover page photos with layout: 1 large photo on left, 2 stacked on right
 */
export const buildCoverPagePhotosHTML = (photos: CoverPagePhotoData[]): string => {
  if (photos.length === 0) return '';

  const photo1 = photos[0];
  const photo2 = photos[1];
  const photo3 = photos[2];

  // Layout: 1 large photo on left (wider, shorter), 2 stacked on right (narrower)
  // Both columns have equal height, left image height = combined height of right images
  // Fixed container height to ensure equal column heights
  const containerHeight = '365px'; // Reduced to keep cover page on one page when photos are present
  const captionHeight = '16px'; // Slightly reduced caption height
  const gapBetweenRightPhotos = '1px';
  const leftColumnWidth = '65%'; // Increased from 58% to make left image wider
  const rightColumnWidth = '35%'; // Decreased from 42% to make right column narrower

  // Right column: total image area = container height - 2 captions - gap
  // This gives us the combined height of both right images
  const rightImagesCombinedHeight = `calc(${containerHeight} - ${captionHeight} - ${captionHeight} - ${gapBetweenRightPhotos})`;
  // Each right image gets half of the combined height
  const rightImageHeight = `calc(${rightImagesCombinedHeight} / 2)`;
  // Left image height = container height - caption (to match right column total height)
  // This ensures both columns have exactly the same total height
  const leftImageHeight = `calc(${containerHeight} - ${captionHeight})`;
  // Left column total = left image + caption (should equal containerHeight)
  // This ensures both columns have the same total height

  return `
    <div style="padding: 10px 10px; margin: 10px 0px; display: flex; gap: 15px; height: ${containerHeight}; align-items: stretch;">
      ${
        photo1
          ? `
        <!-- Large Photo (Left) -->
        <div style="flex: 0 0 ${leftColumnWidth}; display: flex; flex-direction: column; height: 100%;">
          <div style="width: 100%; height: ${leftImageHeight}; border-radius: 8px; overflow: hidden; position: relative;">
            <img src="${photo1.url}" alt="${photo1.caption || ''}" style="width: 100%; height: 100%; object-fit: cover; display: block;" />
          </div>
          ${
            photo1.caption
              ? `
            <div style="height: ${captionHeight}; margin-top: 4px; text-align: center; font-size: 15px; color: #333; display: flex; align-items: center; justify-content: center;">
              ${photo1.caption}
            </div>
          `
              : `<div style="height: ${captionHeight};"></div>`
          }
        </div>
      `
          : ''
      }
      
      ${
        photo2 || photo3
          ? `
        <!-- Stacked Photos (Right) -->
        <div style="flex: 1; display: flex; flex-direction: column; gap: ${gapBetweenRightPhotos}; height: 100%;">
          ${
            photo2
              ? `
            <div style="flex: 1; display: flex; flex-direction: column; min-height: 0;">
              <div style="width: 100%; height: ${rightImageHeight}; border-radius: 8px; overflow: hidden; position: relative;">
                <img src="${photo2.url}" alt="${
                  photo2.caption || ''
                }" style="width: 100%; height: 100%; object-fit: cover; display: block;" />
              </div>
              ${
                photo2.caption
                  ? `
                <div style="height: ${captionHeight}; margin-top: 0px; text-align: center; font-size: 15px; color: #333; display: flex; align-items: center; justify-content: center;">
                  ${photo2.caption}
                </div>
              `
                  : `<div style="height: ${captionHeight};"></div>`
              }
            </div>
          `
              : `<div style="flex: 1;"></div>`
          }
          
          ${
            photo3
              ? `
            <div style="flex: 1; display: flex; flex-direction: column; min-height: 0;">
              <div style="width: 100%; height: ${rightImageHeight}; border-radius: 8px; overflow: hidden; position: relative;">
                <img src="${photo3.url}" alt="${
                  photo3.caption || ''
                }" style="width: 100%; height: 100%; object-fit: cover; display: block;" />
              </div>
              ${
                photo3.caption
                  ? `
                <div style="height: ${captionHeight}; margin-top: 4px; text-align: center; font-size: 15px; color: #333; display: flex; align-items: center; justify-content: center;">
                  ${photo3.caption}
                </div>
              `
                  : `<div style="height: ${captionHeight};"></div>`
              }
            </div>
          `
              : `<div style="flex: 1;"></div>`
          }
        </div>
      `
          : ''
      }
    </div>
  `;
};
